import { CaretLeft, CaretRight, IconContext } from "@phosphor-icons/react";
import { sand } from "@radix-ui/colors";
import {
  Box,
  Flex,
  Grid,
  IconButton,
  Progress,
  ScrollArea,
  Skeleton,
  Text,
} from "@radix-ui/themes";
import { useQuery } from "@tanstack/react-query";
import cx from "classnames";
import Hls from "hls.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useWorkflow } from "./context";

import { getProject } from "../../services/projects";
import { isNil } from "../../utils/fp";
import { renderPersonaAvatar } from "../../utils/helpers/ai-persona-avatars";
import { renderFileIcon } from "../../utils/helpers/files";
import { useMutationObserver } from "../../utils/hooks/useMutationObserver";

const panelClasses =
  "rounded-lg bg-base-white p-4 sm:p-6 max-h-[calc(100vh_-_90px)]";

export default function PreviewPane() {
  const {
    assetPreviewIndex,
    assetsCount,
    incrementPreviewIndex,
    decrementPreviewIndex,
    reviewDocuments,
    folder,
  } = useWorkflow();

  const [documentHeight, setDocumentHeight] = useState("100%");
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { height } = entry.contentRect; // height minus padding
        const titleHeight = 26;
        const totalGapHeight = 32 * (assetsCount === 1 ? 1 : 2);
        const carouselHeight = assetsCount === 1 ? 0 : 82;
        const finalHeight =
          height - titleHeight - totalGapHeight - carouselHeight;

        setDocumentHeight(`${finalHeight}px`);
      }
    });

    if (panelRef.current) {
      resizeObserver.observe(panelRef.current);
    }

    return () => {
      if (panelRef.current) {
        resizeObserver.unobserve(panelRef.current);
      }
    };
  }, [assetsCount]);

  if (assetsCount === 0) {
    return <SkeletonPane />;
  }

  // TODO: Get updated data once Samba finishes processing.
  let displayedAssetCount = assetsCount;
  for (const ticket of folder?.tickets ?? []) {
    if (ticket.lastRevisionBoard.documentImportType === "ZIP") {
      displayedAssetCount +=
        Math.max(ticket.lastRevisionBoard.reviewFiles?.length ?? 0, 1) - 1;
    }
  }

  return (
    <Box data-testid="preview-pane" ref={panelRef} className={panelClasses}>
      <Grid gap="6" height="100%" className="auto-rows-[auto_1fr]">
        <Text size="4" weight="medium" className="max-w-max">
          {reviewDocuments.every((doc) => doc.isDone)
            ? "Reviewed"
            : "Reviewing"}{" "}
          {displayedAssetCount.toLocaleString()} Asset
          {displayedAssetCount === 1 ? "" : "s"}
        </Text>

        <Grid gap="4" height="100%" className="auto-rows-[1fr_auto]">
          <Box height={documentHeight}>
            <Preview />
          </Box>

          <Grid
            gap="2"
            className={cx("max-w-max", { hidden: assetsCount === 1 })}
          >
            <Flex gap="2" align="center">
              <IconButton
                size="1"
                variant="outline"
                disabled={assetsCount === 1}
                onClick={() => decrementPreviewIndex()}
              >
                <CaretLeft />
              </IconButton>
              <IconButton
                size="1"
                variant="outline"
                disabled={assetsCount === 1}
                onClick={() => incrementPreviewIndex()}
              >
                <CaretRight />
              </IconButton>

              <Text size="1">
                {assetsCount === displayedAssetCount ? "Asset" : "Group"}{" "}
                {assetPreviewIndex + 1} of {assetsCount}
              </Text>
            </Flex>

            <ScrollArea
              scrollbars="horizontal"
              type="hover"
              style={{ width: "100%" }}
            >
              <Flex gap="2" className="hidden sm:flex">
                {reviewDocuments.map((doc, i) => (
                  <Grid gap="1" key={`.${i}`} className="w-[4.5rem]">
                    <Skeleton loading={doc.processStatus.isPending}>
                      <img
                        src={doc.thumbnails[0]}
                        alt={`${doc.ticketId} thumbnail`}
                        className={cx(
                          "h-10 w-[4.5rem] rounded-md border-2 object-cover p-px",
                          {
                            "border-puntt-blue-10": i === assetPreviewIndex,
                            "border-transparent": i !== assetPreviewIndex,
                          },
                        )}
                      />
                    </Skeleton>

                    <Progress
                      value={doc.isDone ? 100 : 0}
                      duration="120s"
                      className={cx(
                        "opacity-100 transition-opacity duration-1000",
                        {
                          "opacity-0": doc.isDone,
                        },
                      )}
                    />
                  </Grid>
                ))}
              </Flex>
            </ScrollArea>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

function SkeletonPane() {
  return (
    <Box data-testid="preview-pane-skeleton" className={panelClasses}>
      <Grid gap="6" height="100%" className="auto-rows-[auto_1fr]">
        <Skeleton>
          <Text size="4" weight="medium" className="max-w-max">
            Reviewing 0 Assets
          </Text>
        </Skeleton>

        <Grid gap="4" height="100%" className="auto-rows-[1fr_auto]">
          <Skeleton>
            <Box height="100%" />
          </Skeleton>

          <Grid gap="2" className="max-w-max">
            <Flex gap="2" align="center">
              <Skeleton>
                <IconButton size="1">
                  <CaretLeft />
                </IconButton>
              </Skeleton>
              <Skeleton>
                <IconButton size="1">
                  <CaretRight />
                </IconButton>
              </Skeleton>

              <Skeleton>
                <Text size="1">Asset 0 of 0</Text>
              </Skeleton>
            </Flex>

            <Flex gap="2" className="hidden sm:flex">
              {Array.from({ length: 3 }).map((_, i) => (
                <Grid gap="1" key={`.${i}`}>
                  <Skeleton>
                    <Box className="aspect-video h-10" />
                  </Skeleton>

                  <Skeleton>
                    <Progress />
                  </Skeleton>
                </Grid>
              ))}
            </Flex>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

function Preview() {
  const { folder, reviewDocuments, assetPreviewIndex } = useWorkflow();

  const currentTicket = (folder?.tickets ?? []).find(
    (t) => t._id === reviewDocuments[assetPreviewIndex].ticketId,
  );

  if (isNil(currentTicket)) {
    return null;
  }

  const fileExtension =
    currentTicket.lastRevisionBoard.documentImportType.toLowerCase();

  switch (fileExtension) {
    case "pdf":
    case "jpg":
    case "jpeg":
    case "png":
    case "doc":
    case "docx":
    case "ppt":
    case "pptx":
    case "zip":
      return <DocumentPreview />;
    case "mp4":
    case "mov":
    case "wmv":
    case "webm":
    case "avi":
    case "flv":
    case "mkv":
    case "m3u8":
      return <VideoPreview />;
    default:
      return (
        <Grid className="place-content-center" height="100%">
          <IconContext.Provider
            value={{
              size: 128,
              className: "mx-auto",
              color: sand.sand8,
            }}
          >
            {renderFileIcon(fileExtension)}
          </IconContext.Provider>
        </Grid>
      );
  }
}

function DocumentPreview() {
  const { reviewDocuments, assetPreviewIndex, pendingCommentIndex } =
    useWorkflow();

  const ref = useRef<HTMLDivElement>(null);

  const currentDocument = reviewDocuments[assetPreviewIndex];
  const isShowingFinals = currentDocument.finalComments.length > 0;
  const commentsShown = isShowingFinals
    ? currentDocument.finalComments
    : currentDocument.pendingComments;

  const lastComment = currentDocument?.pendingComments?.at(pendingCommentIndex);
  const lastCommentId = lastComment?._id;

  const observerCallback = useCallback(() => {
    if (!lastCommentId) return;

    const el = ref.current?.querySelector(`[data-id="${lastCommentId}"]`);

    el?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [lastCommentId]);

  useMutationObserver(ref, observerCallback);

  // Call the callback when lastCommentId changes
  useEffect(() => {
    observerCallback();
  }, [lastCommentId, observerCallback]);

  return (
    <ScrollArea type="always" scrollbars="vertical" ref={ref}>
      <Flex direction="column" gap="1" className="document-container relative">
        {currentDocument.thumbnails.map((key, i) => (
          <Box key={key} className="relative">
            <img src={key} alt={`Page ${i + 1}`} width="100%" />
            {commentsShown
              ?.filter((c) => c.page_num === i + 1)
              .map(({ persona, _id, x, y, required }) => (
                <Box
                  key={_id}
                  data-id={_id}
                  data-required={required}
                  data-final={isShowingFinals}
                  className="absolute rounded-full border-4 data-[required=false]:border-base-white data-[required=true]:border-puntt-red-6 data-[final=false]:opacity-50"
                  style={{ left: `${x * 100}%`, top: `${y * 100}%` }}
                >
                  {renderPersonaAvatar(persona, true)}
                </Box>
              ))}
          </Box>
        ))}
      </Flex>
    </ScrollArea>
  );
}

function VideoPreview() {
  const { reviewDocuments, assetPreviewIndex } = useWorkflow();
  const currentVideoDocument = reviewDocuments[assetPreviewIndex];
  const previewThumbnail = currentVideoDocument.thumbnails[0];

  const ticketQuery = useQuery({
    queryKey: ["tickets", currentVideoDocument.ticketId],
    queryFn: () => getProject(currentVideoDocument.ticketId),
  });

  const videoSrc =
    // TODO: Currently only one revision board is supported.
    ticketQuery.data?.revisionBoards?.[0].reviewFiles?.[0]?.source ?? "";

  const videoDuration = useHlsDuration(
    videoSrc,
    currentVideoDocument.processStatus.isAIReady,
  );
  const lastCommentTimestamp =
    currentVideoDocument.pendingComments?.at(-1)?.timestamp ?? 0;

  return (
    <figure className="h-[calc(100%_-_40px)]">
      {currentVideoDocument.processStatus.isPending ? (
        <Skeleton>
          <Box height="100%" />
        </Skeleton>
      ) : (
        <img
          src={previewThumbnail}
          alt="Video thumbnail"
          className="mb-1 max-h-full w-full object-contain"
        />
      )}

      <Progress
        value={videoDuration === 0 ? 0 : lastCommentTimestamp / videoDuration}
        mb="1"
      />
      {/* Comment pins go here. */}
      <Box className="relative h-10 w-full">
        {currentVideoDocument.isDone
          ? currentVideoDocument.finalComments.map((comment) => (
              <Box
                key={comment._id}
                style={{
                  position: "absolute",
                  left: `${(comment.timestamp / videoDuration) * 100}%`,
                }}
              >
                {renderPersonaAvatar(comment.persona)}
              </Box>
            ))
          : currentVideoDocument.pendingComments?.map((comment) => (
              <Box
                key={comment._id}
                style={{
                  position: "absolute",
                  left: `${(comment.timestamp / videoDuration) * 100}%`,
                }}
              >
                {renderPersonaAvatar(comment.persona)}
              </Box>
            ))}
      </Box>
    </figure>
  );
}

function useHlsDuration(url: string, enabled: boolean) {
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!enabled) {
      console.debug("Video is not fully processed yet");
      return;
    }

    if (!url) {
      console.debug("No video source");
      setDuration(0);
      return;
    }

    if (!url.toLowerCase().endsWith(".m3u8")) {
      console.debug("Not an HLS video");
      setDuration(0);
      return;
    }

    console.debug("Getting video duration");
    const video = document.createElement("video");
    video.preload = "metadata";
    video.style.position = "absolute";
    video.style.left = "-9999px";
    video.style.opacity = "0";
    video.muted = true;
    video.tabIndex = -1;
    document.body.appendChild(video);

    let hls: Hls;

    // Native HLS support (Safari)
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      console.debug("Probably Safari");
      video.src = url;

      video.addEventListener("loadedmetadata", () => {
        console.debug("Loaded metadata", video.duration);
        setDuration(video.duration);
        if (video.duration && video.duration !== Infinity) {
          video.remove();
        }
      });

      video.addEventListener("durationchange", () => {
        console.debug("Duration changed", video.duration);
        setDuration(video.duration);
        if (video.duration && video.duration !== Infinity) {
          video.remove();
        }
      });

      video.addEventListener("error", (err) => {
        console.error("Error", err);
        setDuration(0);
        video.remove();
      });

      document.body.appendChild(video);
    } else if (Hls.isSupported()) {
      console.debug("HLS", url);
      hls = new Hls();
      hls.loadSource(url);
      hls.attachMedia(video);

      video.addEventListener("loadedmetadata", () => {
        console.debug("Loaded metadata", video.duration);
        setDuration(video.duration);
        if (video.duration && video.duration !== Infinity) {
          hls.destroy();
          video.remove();
        }
      });

      video.addEventListener("durationchange", () => {
        console.debug("Duration changed", video.duration);
        setDuration(video.duration);
        if (video.duration && video.duration !== Infinity) {
          hls.destroy();
          video.remove();
        }
      });

      video.addEventListener("error", (err) => {
        console.error("Error", err);
        setDuration(0);
        hls.destroy();
        video.remove();
      });

      setTimeout(() => {
        video.muted = true;
        video.play().catch((e) => console.warn("Autoplay prevented:", e));
      }, 5000);
    } else {
      console.debug("No HLS support");
      setDuration(0);
    }

    // Cleanup
    return () => {
      if (hls) hls.destroy();
      video.remove();
      console.debug("Cleanup");
    };
  }, [enabled, url]);

  return useMemo(() => duration, [duration]);
}
